import * as React from 'react';
import * as _ from 'underscore';

import { IParameterFieldBaseOptions } from '../ts/ParameterFieldBase';

export interface IStringFieldOptions extends IParameterFieldBaseOptions {
    changeOnBlurOnly?: boolean;
    inline?: boolean;
    passwordField?: boolean;
    applyOnEnter?: boolean;
    placeholder?: string;
}

export interface IStringFieldProperties<TOptions extends IStringFieldOptions = IStringFieldOptions> {
    value?: string;
    placeholder?: string;
    title?: string;
    themeValue?: string;
    maxLength?: number;
    allowNull?: boolean;
    setFocus?: boolean;
    changeOnBlurOnly?: boolean;
    delayChangeInterval?: number;
    readOnly?: boolean;
    passwordField?: boolean;
    applyOnEnter?: boolean;
    options?: TOptions;

    onChange?(value: string): void;
    onEnterPress?(value: string): void;
    onBlur?(value: string): void;
    onTab?(value: string): void;
    onFocus?(value: string): void;
}

export interface IStringFieldStatelessProperties extends IStringFieldProperties {
    inputControlRef?: React.RefObject<HTMLInputElement>;
    isNotFormControl?: boolean;
    onSetState?(value: string): void;
}

export function StringFieldStateless(props: IStringFieldStatelessProperties) {
    const [seePassword, setSeePassword] = React.useState(false);

    const delayedOnChange = React.useRef<NodeJS.Timer>(null);
    const changeIsNotApplied = React.useRef(false);

    const getTargetValue = (e) => {
        let result = e.target.value;

        if (result === '') {
            if (props.themeValue === undefined) {
                result = null;
            }
        }

        return result;
    };

    const getReturnValue = (value: string) => {
        if (
            (props.allowNull != null && props.allowNull !== false) ||
            (props.options != null && props.options.allowNull !== false)
        ) {
            return value;
        } else {
            return value != null ? value : '';
        }
    };

    const riseOnChange = (value: string) => {
        if (typeof props.onChange === 'function') {
            if (props.themeValue !== undefined) {
                props.onChange(value === '' ? null : value);
            } else {
                props.onChange(getReturnValue(value));
            }
        }
    };

    const changeOnBlurOnlyIsSet = () => {
        return props.changeOnBlurOnly || props.options?.changeOnBlurOnly;
    };

    const handleOnChange = (e) => {
        const newValue = getTargetValue(e);

        if (props.onChange != null && !changeOnBlurOnlyIsSet()) {
            if (props.delayChangeInterval > 0) {
                if (delayedOnChange.current != null) {
                    clearTimeout(delayedOnChange.current);
                }

                const timer = setTimeout(() => {
                    processOnChange(newValue);
                }, props.delayChangeInterval);

                delayedOnChange.current = timer;
            } else {
                riseOnChange(newValue);
            }
        }

        if (props.value !== newValue) {
            changeIsNotApplied.current = true;
            props.onSetState?.(newValue);
        }
    };

    const handleOnKeyDown = (e) => {
        if (e.code === 'Tab') {
            if (typeof props.onTab === 'function') {
                const newValue = getTargetValue(e);

                props.onTab(newValue);
            }
        } else if (e.code === 'Enter' || e.code === 'NumpadEnter') {
            const newValue = getTargetValue(e);

            if (props.applyOnEnter || (props.options != null && props.options.applyOnEnter)) {
                processOnChange(newValue);
            }

            if (typeof props.onEnterPress === 'function') {
                props.onEnterPress(getReturnValue(newValue));
            }
        }
    };

    const processOnChange = (newValue: string) => {
        if (delayedOnChange.current != null) {
            clearTimeout(delayedOnChange.current);
            delayedOnChange.current = null;
        }

        changeIsNotApplied.current = false;
        props.onSetState?.(newValue);
        riseOnChange(newValue);
    };

    const handleOnBlur = (e) => {
        if (changeIsNotApplied.current) {
            const newValue = getTargetValue(e);

            processOnChange(newValue);
        }

        if (props.onBlur != null) {
            const newValue = getTargetValue(e);

            props.onBlur(newValue);
        }
    };

    const handleOnFocus = (e) => {
        if (props.onFocus != null) {
            const newValue = getTargetValue(e);

            props.onFocus(newValue);
        }
    };

    const options = props.options;

    //LL: 20.03.18
    //EVG: It was done to make the field hidden when using it via TS wrapper. The wrapper still shows its title. Review it later.
    //LL:  Yes, to display only title
    const hidden = options != null && options.hidden === true;

    if (hidden) {
        return null;
    }

    let passwordField = props.passwordField;
    let readOnly = props.readOnly;
    let isInline = false;
    let placeholder = props.placeholder;

    if (options != null) {
        if (passwordField == null) {
            passwordField = options.passwordField;
        }

        if (readOnly == null) {
            readOnly = options.readOnly;
        }

        if (placeholder == null) {
            placeholder = options.placeholder;
        }

        isInline = options.inline;
    }

    let clearButton: React.JSX.Element;
    let inheritedButton: React.JSX.Element;
    let displayValue: string;

    if (props.themeValue !== undefined) {
        if (props.value != null && props.value !== props.themeValue) {
            clearButton = (
                <div
                    className="glyphicon glyphicon-remove clear-button"
                    onClick={() => {
                        props.onSetState?.(null);
                    }}
                ></div>
            );
        }

        if (clearButton == null) {
            inheritedButton = (
                <div
                    className="glyphicon glyphicon-arrow-down clear-button"
                    title={'Inherited from parent theme'}
                ></div>
            );
        }

        displayValue = props.value != null ? props.value : props.themeValue;
    } else {
        displayValue = props.value;
    }

    if (displayValue == null) {
        displayValue = '';
    }

    const inputStyle: React.CSSProperties = {};
    const inputType = passwordField && !seePassword ? 'password' : 'text';
    // OE: fixes Chrome autocompletion issue
    // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
    const autocomplete = passwordField ? 'new-password' : null;

    let inputElement: React.JSX.Element;

    if (readOnly) {
        const style: React.CSSProperties = {
            ...inputStyle,
            cursor: 'default'
        };

        inputElement = (
            <input
                className={props.isNotFormControl ? undefined : 'form-control'}
                type={inputType}
                style={style}
                value={displayValue}
                readOnly={true}
                placeholder={placeholder}
                title={props.title}
                autoComplete={autocomplete}
                tabIndex={-1}
            />
        );
    } else {
        inputElement = (
            <input
                className={props.isNotFormControl ? undefined : 'form-control'}
                type={inputType}
                ref={props.inputControlRef}
                style={{ ...inputStyle }}
                value={displayValue}
                placeholder={placeholder}
                title={props.title}
                autoComplete={autocomplete}
                tabIndex={0}
                maxLength={props.maxLength}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                onInput={handleOnChange}
                onKeyDown={handleOnKeyDown}
                onChange={() => {}} // Is added to fix red warning.
            />
        );

        if (passwordField) {
            inputElement = (
                <div>
                    <div className="password-text">{inputElement}</div>
                    <div
                        className={'password-eye ' + (seePassword ? 'bi bi-eye' : 'bi bi-eye-slash')}
                        onMouseDown={() => {
                            setSeePassword(true);
                        }}
                        onMouseUp={() => {
                            setSeePassword(false);
                        }}
                    />
                </div>
            );
        }
    }

    return (
        <div className={'string-field' + (isInline ? ' inline' : '')}>
            {clearButton}
            {inheritedButton}
            {inputElement}
        </div>
    );
}

function normalizeValue(value): string {
    if (value == null || value === '') {
        return null;
    }

    if (Array.isArray(value)) {
        return value.length > 0 ? value[0] : null;
    } else {
        return value.toString();
    }
}

export default function StringField(props: IStringFieldProperties) {
    const [value, setValue] = React.useState(normalizeValue(props.value));

    const inputControlRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (props.setFocus ?? props.options?.setFocus) {
            if (inputControlRef.current != null) {
                setTimeout(() => {
                    if (inputControlRef.current != null) {
                        inputControlRef.current.focus();
                    }
                }, 20);
            }
        }
    }, [props.setFocus, props.options?.setFocus]);

    React.useEffect(() => {
        const newValue = normalizeValue(props.value);

        if (newValue !== value) {
            setValue(newValue);
        }
    }, [props.value]);

    const handleOnSetState = React.useCallback((newValue: string) => {
        setValue(newValue);
    }, []);

    return (
        <StringFieldStateless
            {...props}
            value={value}
            inputControlRef={inputControlRef}
            onSetState={handleOnSetState}
        />
    );
}
